import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import GoogleButton from "@/components/GoogleButton";

export function CheckoutAuthDialog({ open, onOpenChange, onAuthed, nextPath, defaultEmail = "", defaultName = "" }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        await register({ name, email, password, role: "traveler" });
      } else {
        await login(email, password);
      }
      toast.success("You're signed in — completing your booking...");
      onAuthed?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-md" data-testid="checkout-auth-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "signup" ? "Create an account to book" : "Log in to complete your booking"}</DialogTitle>
          <DialogDescription className="text-xs text-[#717171]">
            Your dates, guests and details are saved — you won't lose your progress.
          </DialogDescription>
        </DialogHeader>

        <div className="flex bg-[#F7F7F9] rounded-xl p-1 mb-1">
          <button type="button" data-testid="checkout-auth-login-tab" onClick={() => setMode("login")} className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${mode === "login" ? "bg-white shadow text-[#222]" : "text-[#717171]"}`}>Log in</button>
          <button type="button" data-testid="checkout-auth-signup-tab" onClick={() => setMode("signup")} className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${mode === "signup" ? "bg-white shadow text-[#222]" : "text-[#717171]"}`}>Sign up</button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && (
            <div>
              <Label className="text-sm mb-1 block">Full name</Label>
              <Input data-testid="checkout-auth-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="rounded-xl" />
            </div>
          )}
          <div>
            <Label className="text-sm mb-1 block">Email</Label>
            <Input data-testid="checkout-auth-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="rounded-xl" />
          </div>
          <div>
            <Label className="text-sm mb-1 block">Password</Label>
            <Input data-testid="checkout-auth-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === "signup" ? "At least 6 characters" : "Your password"} className="rounded-xl" />
          </div>
          <Button data-testid="checkout-auth-submit" type="submit" disabled={loading} className="w-full rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white h-11">
            {loading ? "Please wait..." : (mode === "signup" ? "Sign up & continue" : "Log in & continue")}
          </Button>
        </form>

        <div className="relative my-1 text-center">
          <span className="bg-white px-3 text-xs text-[#717171] relative z-10">or</span>
          <div className="absolute inset-x-0 top-1/2 h-px bg-[#EBEBEB]" />
        </div>

        <GoogleButton role="traveler" next={nextPath} label="Continue with Google" />
      </DialogContent>
    </Dialog>
  );
}

export default CheckoutAuthDialog;
